import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useCachedAccess } from '@/lib/access/use-access';
import { NAV_PATHS } from '@/lib/access/route-permissions';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { visibleSections } from './nav-model';
import { SidebarFooter } from './sidebar-footer';

// The expanded sidebar per docs/ui/operator-ui-tds.md §4.3: every console page from
// `md` up, and the phone drawer (`mobile`). Its items, their order and the permission
// filter live in `nav-model.ts`, shared with the icon rail (`sidebar-rail.tsx`) that
// stands beside the full-screen «Operar la disrupción» on a desktop.

export function Sidebar({
  mobile = false,
  onNavigate,
}: { mobile?: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation('admin');
  const access = useCachedAccess();
  const sections = visibleSections(access.can);

  return (
    <aside
      className={cn(
        'density-sidebar min-h-0 flex-col border-border bg-muted/40',
        mobile ? 'flex w-full flex-1' : 'hidden w-56 shrink-0 border-r md:flex',
      )}
    >
      {/* Sections scroll; the footer below stays pinned (§18 contained scroll). `relative`: a
          scroll region holds its own absolutely placed bits (see app-shell.tsx). */}
      <div className="relative flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-2">
        {access.loading ? <NavSkeleton label={t('access.loading')} /> : null}
        {sections.map(({ id, titleKey, items }) => (
          <nav key={id} aria-label={t(titleKey)} data-testid={`sidebar-${id}`}>
            <h2 className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t(titleKey)}
            </h2>
            <div className="space-y-0.5">
              {items.map(({ id: navId, labelKey, icon: Icon }) => (
                <NavLink
                  key={navId}
                  to={NAV_PATHS[navId]}
                  onClick={onNavigate}
                  data-testid={`nav-${navId}`}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted',
                    )
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden />
                  <span>{t(labelKey)}</span>
                </NavLink>
              ))}
            </div>
          </nav>
        ))}
      </div>
      <SidebarFooter />
    </aside>
  );
}

/** LOADING — access not known yet: placeholder rows, never the wrong menu. */
function NavSkeleton({ label }: { label: string }) {
  return (
    <div className="space-y-2 px-1" role="status" aria-busy="true" data-testid="sidebar-loading">
      <span className="sr-only">{label}</span>
      {[0, 1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}
